import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { initialNegotiationState } from '../initialState/negotiationWebsocket'
import { toast } from 'sonner'
import {
  NegotiationMember,
  NegotiationType,
  ReplyToSnippet,
} from '../types/negotiationWebsocket'
import { Status } from '../types/room'

const negotiationWebsocketSlice = createSlice({
  name: 'negotiationWebsocket',
  initialState: initialNegotiationState,
  reducers: {
    setConnectionStatus(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload
    },
    setWebSocketError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },

    handleNegotiationInvite(state, action) {
      const existingIndex = state.negotiations.findIndex(
        (n) => n.negotiationId == action.payload.negotiation_id
      )

      const mappedMembers =
        action.payload.members?.map((member: NegotiationMember) => ({
          email: member.email,
          name: member.name,
          status: member.status,
          is_inviter:
            member.user_id ===
            (action.payload.inviter_id || member.status === Status.Owner),
        })) || []

      const newNegotiation = {
        negotiationId: action.payload.negotiation_id,
        inviter: action.payload.inviter,
        members: mappedMembers,
        starttime: new Date().toISOString(),
        member_count: action.payload.member_count || mappedMembers.length,
        is_one_on_one:
          action.payload.is_one_on_one ||
          (mappedMembers.length === 2 &&
            action.payload.negotiation_type === NegotiationType.PRIVATE_ROOM),
        negotiation_type:
          action.payload.negotiation_type || NegotiationType.PRIVATE_ROOM,
        phase_id: action.payload.phase_id,
        room_id: action.payload.room_id,
      }

      if (existingIndex !== -1) {
        // Update existing negotiation
        state.negotiations[existingIndex] = {
          ...state.negotiations[existingIndex],
          ...newNegotiation,
        }
      } else {
        state.negotiations.push(newNegotiation)
      }
    },

    handleNegotiationResponse(state, action) {
      const existingIndex = state.negotiations.findIndex(
        (n) => n.negotiationId == action.payload.negotiation_id
      )
      const mappedMembers =
        action.payload.members?.map((member: NegotiationMember) => ({
          email: member.email,
          name: member.name,
          status: member.status,
          is_inviter:
            member.user_id ===
            (action.payload.inviter_id || member.status === Status.Owner),
        })) || []
      const nextNegotiation = {
        negotiationId: action.payload.negotiation_id,
        inviter: action.payload.inviter || '',
        members: mappedMembers,
        starttime: new Date().toISOString(),
        member_count: action.payload.member_count || mappedMembers.length,
        is_one_on_one:
          action.payload.is_one_on_one ||
          (mappedMembers.length === 2 &&
            action.payload.negotiation_type === NegotiationType.PRIVATE_ROOM),
        negotiation_type:
          action.payload.negotiation_type || NegotiationType.PRIVATE_ROOM,
        phase_id: action.payload.phase_id,
        room_id: action.payload.room_id,
        // The server decides whether a response ended the chat: a rejection
        // ends a one-to-one negotiation, but a group chat carries on without
        // the invitee who declined.
        end_time: action.payload.end_time || undefined,
      }

      if (existingIndex !== -1) {
        const negotiation = state.negotiations[existingIndex]
        state.negotiations[existingIndex] = {
          ...negotiation,
          ...nextNegotiation,
        }
        if (
          mappedMembers.length === 0 &&
          state.negotiations[existingIndex].members
        ) {
          const memberIndex = state.negotiations[existingIndex].members.findIndex(
            (member) => member.email === action.payload.responder
          )
          if (memberIndex !== -1) {
            state.negotiations[existingIndex].members[memberIndex].status =
              action.payload.accepted ? Status.Accepted : Status.Rejected
          }
        }
      } else {
        state.negotiations.push(nextNegotiation)
      }

      if (!action.payload.accepted) {
        toast.error(
          `Negotiation has been rejected by ${action.payload.responder}`
        )
      } else {
        toast.success(
          `Negotiation has been accepted by ${action.payload.responder}`
        )
      }
    },

    handleNegotiationTerminated(state, action) {
      const existingIndex = state.negotiations.findIndex(
        (n) => n.negotiationId == action.payload.negotiation_id
      )

      if (existingIndex !== -1) {
        state.negotiations[existingIndex].end_time = new Date().toISOString()
      }

      toast.error('Negotiation has been terminated')
    },

    handleGroupMembersAdded(state, action) {
      const existingIndex = state.negotiations.findIndex(
        (n) => n.negotiationId == action.payload.negotiation_id
      )
      if (existingIndex === -1) return

      const mappedMembers =
        action.payload.members?.map((member: NegotiationMember) => ({
          email: member.email,
          name: member.name,
          status: member.status,
          is_inviter: member.status === Status.Owner,
        })) || []

      state.negotiations[existingIndex] = {
        ...state.negotiations[existingIndex],
        members: mappedMembers,
        member_count: action.payload.member_count || mappedMembers.length,
      }
    },

    setReplyTarget(
      state,
      action: PayloadAction<{
        negotiationId: string
        target: ReplyToSnippet
      }>
    ) {
      state.replyTargets[String(action.payload.negotiationId)] =
        action.payload.target
    },
    clearReplyTarget(state, action: PayloadAction<string>) {
      delete state.replyTargets[String(action.payload)]
    },

    updateNegotiationMessage(
      state,
      action: PayloadAction<{
        type: 'negotiation'
        id: string
        negotiationId: string
        user: string
        message: string
        name: string
        sender_role: string
        user_profile_picture?: string
        phase_id: number
        entrytime: string
        reply_to?: ReplyToSnippet | null
      }>
    ) {
      const messageExists = state.messages.some(
        (existingMsg) => existingMsg.id === action.payload.id
      )

      if (!messageExists) {
        state.messages.push(action.payload)
      }
    },

    handleUserNegotiations(
      state,
      action: PayloadAction<{
        negotiations: any[]
        messages: any[]
        pagination?: any
      }>
    ) {
      const { negotiations, messages, pagination } = action.payload

      const mappedNegotiations = negotiations.map((negotiation) => ({
        negotiationId: negotiation.id,
        inviter: negotiation.inviter || negotiation.inviter__email,
        members: negotiation.members || [],
        starttime: negotiation.starttime || new Date().toISOString(),
        round_number: negotiation.round_number,
        member_count:
          negotiation.member_count || negotiation.members?.length || 0,
        is_one_on_one:
          negotiation.is_one_on_one ||
          (negotiation.members?.length === 2 &&
            negotiation.negotiation_type === NegotiationType.PRIVATE_ROOM),
        negotiation_type:
          negotiation.negotiation_type || NegotiationType.PRIVATE_ROOM,
        end_time: negotiation.end_time,
        phase_id: negotiation.phase_id || negotiation.phase__id,
        room_id: negotiation.room_id,
      }))

      state.negotiations = mappedNegotiations
      state.messages = messages
      state.pagination = pagination || null
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    clearAllNegotiations: (state) => {
      state.negotiations = []
      state.messages = []
      state.unreadByNegotiationId = {}
    },
    incrementNegotiationUnread(state, action: PayloadAction<string>) {
      const key = String(action.payload)
      state.unreadByNegotiationId[key] =
        (state.unreadByNegotiationId[key] || 0) + 1
    },
    clearNegotiationUnread(state, action: PayloadAction<string>) {
      delete state.unreadByNegotiationId[String(action.payload)]
    },
    clearAllNegotiationUnread(state) {
      state.unreadByNegotiationId = {}
    },
    handleMessagesLoaded(
      state,
      action: PayloadAction<{
        messages: any[]
        pagination: any
        negotiation_id?: string
      }>
    ) {
      const { messages, pagination } = action.payload

      const newMessages = messages.filter(
        (newMsg) =>
          !state.messages.some((existingMsg) => existingMsg.id === newMsg.id)
      )

      state.messages = [...newMessages, ...state.messages]
      state.pagination = pagination
      state.loadingMoreMessages = false
    },
    handleNegotiationMessagesLoaded(
      state,
      action: PayloadAction<{
        messages: any[]
        pagination: any
        negotiation_id: string
      }>
    ) {
      const { messages, pagination } = action.payload

      const newMessages = messages.filter(
        (newMsg) =>
          !state.messages.some((existingMsg) => existingMsg.id === newMsg.id)
      )

      state.messages = [...newMessages, ...state.messages]
      state.pagination = pagination
      state.loadingMoreMessages = false
    },

    setLoadingMoreMessages(state, action: PayloadAction<boolean>) {
      state.loadingMoreMessages = action.payload
    },
  },
})

export const {
  setConnectionStatus,
  setWebSocketError,
  handleNegotiationInvite,
  handleNegotiationResponse,
  handleNegotiationTerminated,
  handleGroupMembersAdded,
  setReplyTarget,
  clearReplyTarget,
  updateNegotiationMessage,
  handleUserNegotiations,
  setIsLoading,
  clearAllNegotiations,
  handleMessagesLoaded,
  handleNegotiationMessagesLoaded,
  setLoadingMoreMessages,
  incrementNegotiationUnread,
  clearNegotiationUnread,
  clearAllNegotiationUnread,
} = negotiationWebsocketSlice.actions

export default negotiationWebsocketSlice.reducer
